import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { mockContactSubmission } from '../mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = 'Invalid phone number (10 digits required)';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const response = await mockContactSubmission(formData);
      if (response.success) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <section id="contact" className="section-padding bg-[var(--bg-page)]">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="heading-1 mb-4">Get In Touch</h2>
          <p className="body-large text-[var(--text-secondary)]">
            Have questions about our services? We're here to help you navigate healthcare financing with confidence.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="heading-3 mb-6">Contact Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[var(--accent-blue-200)] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[var(--brand-blue)]" />
                  </div>
                  <div>
                    <p className="font-medium text-[var(--text-primary)] mb-1">Office Address</p>
                    <p className="body-small text-[var(--text-secondary)]">
                      #3 1st Floor, Opp Sahana Vidya Mandir<br />
                      Naganathapura, Bangalore - 560100
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[var(--accent-green-200)] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[var(--brand-green)]" />
                  </div>
                  <div>
                    <p className="font-medium text-[var(--text-primary)] mb-1">Phone Number</p>
                    <a href="tel:7676620620" className="body-small text-[var(--text-secondary)] hover:text-[var(--brand-blue)] transition-colors">
                      7676620620
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[var(--accent-pink-200)] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[var(--brand-blue)]" />
                  </div>
                  <div>
                    <p className="font-medium text-[var(--text-primary)] mb-1">Email Address</p>
                    <a href="mailto:info@infiniti-health.com" className="body-small text-[var(--text-secondary)] hover:text-[var(--brand-blue)] transition-colors">
                      info@infiniti-health.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[var(--accent-blue-200)] rounded-xl p-6">
              <h4 className="font-semibold text-[var(--text-primary)] mb-3">Business Hours</h4>
              <p className="body-small text-[var(--text-secondary)]">
                Monday - Friday: 9:00 AM - 6:00 PM<br />
                Saturday: 9:00 AM - 1:00 PM<br />
                Sunday: Closed
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[var(--accent-green-200)] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[var(--brand-green)]" />
                </div>
                <h3 className="heading-3 mb-2">Message Sent Successfully!</h3>
                <p className="body-medium text-[var(--text-secondary)]">
                  Thank you for contacting us. We'll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block body-small font-medium text-[var(--text-primary)] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-500' : 'border-[var(--border-input)]'} focus:border-[var(--border-input-focus)] focus:outline-none transition-colors`}
                    placeholder="John Doe"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block body-small font-medium text-[var(--text-primary)] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-[var(--border-input)]'} focus:border-[var(--border-input-focus)] focus:outline-none transition-colors`}
                    placeholder="john@example.com"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block body-small font-medium text-[var(--text-primary)] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-[var(--border-input)]'} focus:border-[var(--border-input-focus)] focus:outline-none transition-colors`}
                    placeholder="9876543210"
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label htmlFor="subject" className="block body-small font-medium text-[var(--text-primary)] mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${errors.subject ? 'border-red-500' : 'border-[var(--border-input)]'} focus:border-[var(--border-input-focus)] focus:outline-none transition-colors`}
                    placeholder="Reimbursement Assistance Inquiry"
                  />
                  {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                </div>

                <div>
                  <label htmlFor="message" className="block body-small font-medium text-[var(--text-primary)] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className={`w-full px-4 py-3 rounded-lg border ${errors.message ? 'border-red-500' : 'border-[var(--border-input)]'} focus:border-[var(--border-input-focus)] focus:outline-none transition-colors resize-none`}
                    placeholder="Tell us about your inquiry..."
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
